// A split pane's tab strip. Split out of Centre.tsx (hotspot decomposition);
// behaviour unchanged.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type { Leaf } from "../../../components/Navigation/paneTree.js";
import { tabSetOf } from "../../../components/Navigation/paneTree.js";
import { ResourceTabs } from "../../../components/ResourceTabs/ResourceTabs.js";
import { useAppShell } from "../context.js";

export const PaneStrip = ({ leaf }: { leaf: Leaf }): ReactNode => {
    const { t } = useLingui();
    const {
        dispatch,
        closeHome,
        closeSearch,
        mkConversationId,
        conversationTitleFor,
        closeSettings,
    } = useAppShell();
    return (
        <ResourceTabs
            tabs={tabSetOf(leaf)}
            conversationTitleFor={conversationTitleFor}
            settingsTitle={t`Settings`}
            homeTitle={t`Home`}
            searchTitle={t`Search`}
            onActivate={(tab) =>
                dispatch({ type: "activate-in-pane", pane: leaf.id, tab })
            }
            onClose={(id) => {
                dispatch({
                    type: "activate-in-pane",
                    pane: leaf.id,
                    tab: { type: "resource", id },
                });
                dispatch({
                    type: "close-tab",
                    id,
                    freshConversationId: mkConversationId(),
                });
            }}
            onCloseOthers={(id) => dispatch({ type: "close-other-tabs", id })}
            onCloseToRight={(id) =>
                dispatch({ type: "close-tabs-to-right", id })
            }
            onCloseConversation={(conversationId) => {
                dispatch({
                    type: "activate-in-pane",
                    pane: leaf.id,
                    tab: { type: "conversation", id: conversationId },
                });
                dispatch({
                    type: "close-conversation-tab",
                    conversationId,
                    freshConversationId: mkConversationId(),
                });
            }}
            onCloseSettings={closeSettings}
            onCloseHome={closeHome}
            onCloseSearch={closeSearch}
            onCloseQuality={(materialId) => {
                // Focus this pane + tab first (mirrors onCloseConversation), so
                // the close-quality-report fold acts on the right leaf.
                dispatch({
                    type: "activate-in-pane",
                    pane: leaf.id,
                    tab: { type: "quality", materialId },
                });
                dispatch({
                    type: "close-quality-report",
                    materialId,
                    freshConversationId: mkConversationId(),
                });
            }}
            onNewTab={() =>
                dispatch({
                    type: "new-tab",
                    conversationId: mkConversationId(),
                })
            }
            preview={leaf.preview}
            onActivatePreview={() => {
                if (leaf.preview !== null)
                    dispatch({
                        type: "open-resource-preview",
                        ref: leaf.preview,
                    });
            }}
            onPromotePreview={() => dispatch({ type: "promote-preview-tab" })}
            onClosePreview={() => dispatch({ type: "close-preview" })}
        />
    );
};
